import env from '@/assets/env';

import { stringify } from 'flatted';
import { GetCurrentUserToken } from '@/lib/db/auth';

import csvToBlob from '@/lib/csvToBlob';
import pdfToBlob from '@/lib/pdfToBlob';
import bufferToBlob from './bufferToBlob';

const path = ( a, queryOptions = null ) => {
	const query = ( () => {
		if ( !queryOptions ) {
			return '';
		}

		const queryKeys   = Object.keys( queryOptions );
		const queryString = queryKeys
			.map( ( key ) => {

				const value = queryOptions[key];
				if ( typeof value !== 'object' ) {
					return `${key}=${encodeURIComponent( queryOptions[key] )}`;
				}

				return encodeArray( key, value );
			} )
			.filter( q => q.length )
			.join( '&' );

		return `?${queryString}`;
	} )();

	return `${env.API_PROTOCOL}://${env.API_URL}/${a}${query}`;
};

const encodeArray = ( key, arr ) => { // eslint-disable-line

	return arr.reduce( ( a, item, i ) => {

		// For 1 dimensional arrays
		if ( !Array.isArray( item ) ) {
			a.push( `${key}[${i}]=${encodeURIComponent( item )}` );
			return a;
		}

		// For 2 dimensional arrays
		item.forEach( ( x, j ) => {
			if ( typeof x === 'object' ) {
				const objKeys = Object.keys( x );
				objKeys.forEach( ( objKey ) => {
					a.push( `${key}[${i}][${j}][${objKey}]=${encodeURIComponent( x[objKey] )}` );
				} );

				return;
			}
			a.push( `${key}[${i}][${j}]=${encodeURIComponent( x )}` );
		} );

		return a;

	}, [] )
		.join( '&' );
};

/**
 * a courier function which will run
 * it's first paramater as a callback
 * with a Firebase AuthToken added in
 * at the beginning of the paramaters
 * list
 *
 * @param method (function) the method to be called after a token is generated
 */
const authenticate = method => ( ...args ) => GetCurrentUserToken()
	.then( ( token ) => {
		const authObject = {
			token,
			defaultHeaders : new Headers( {
				'Auth-Token'   : token,
				'Content-Type' : 'application/json',
			} ),
		};
		const allArgs = [authObject].concat( args );

		return method( ...allArgs );
	} );

// simply uses a try/catch block to
// ensure standard behaviors on
// fetch
const fetchPromise = ( route, options ) => new Promise( ( resolve, reject ) => {
	try {
		fetch( route, options )
			.then( async ( res ) => {
				const status = parseInt( res.status, 10 );
				if ( ( Math.floor( status / 100 ) * 100 ) !== 200 ) {
					reject( res );

					return;
				}

				const body = await _body( res );
				resolve( body, res );
			} )
			.catch( e => reject( e ) );
	}
	catch ( e ) {
		reject( e );
	}
} );

const _body = async ( res ) => {
	const text = await res.text();

	const firstChar = text.charAt( 0 );
	const isJson    = ( firstChar === '{' || firstChar === '[' );

	if ( isJson ) {
		return JSON.parse( text );
	}

	return text;
};

// ***************** User Routes ***************** //

export const GetUsers = authenticate( _auth => fetchPromise( path( 'users' ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const createUser = authenticate( ( _auth, user ) => fetchPromise( path( 'users' ), {
	method  : 'POST',
	body    : JSON.stringify( user ),
	headers : _auth.defaultHeaders,
} ) );

// eslint-disable-next-line camelcase
export const GetUserByUID = authenticate( ( _auth, UID ) => fetchPromise( path( `users/uid/${UID}` ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );
export const editUser = authenticate( ( _auth, user, uid ) => fetchPromise( path( `users/${uid}` ), {
	method  : 'PUT',
	body    : JSON.stringify( user.data ),
	headers : _auth.defaultHeaders,
} ) );

export const GDriveLinkUpdate = authenticate( ( _auth, update ) => fetchPromise( path( 'GDriveLinkUpdate' ), {
	method : 'POST',
	body   : JSON.stringify( {
		email     : update.email,
		driveLink : update.driveLink
	} ),
	headers : _auth.defaultHeaders
} ) );

export const GetStatuses = authenticate( _auth => fetchPromise( path( 'statuses' ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const userNotifications = authenticate( ( _auth, status, usersKey ) => fetchPromise( path( `users/${usersKey}/notification-status` ), {
	method  : 'PUT',
	body    : JSON.stringify( { on : status } ),
	headers : _auth.defaultHeaders,
} ) );

// ***************** Form Component Routes ***************** //

const mapToDropdown = arr => arr.map( a => ( {
	text  : a.title,
	value : a.value,
} ) );

export const GetJobsDropdown = authenticate( _auth => fetchPromise( path( 'jobs/dropdown' ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const GetFacilitiesDropdown = authenticate( _auth => fetchPromise( path( 'facilities/dropdown' ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const GetFacilitiesTypesDropdown = authenticate( _auth => fetchPromise( path( 'facilities/facilities-types/dropdown' ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const GetPipeTypes = authenticate( _auth => fetchPromise( path( 'pipe-types' ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ).then( mapToDropdown ) );

export const GetReviewTypes = authenticate( _auth => fetchPromise( path( 'developer-utility-reviews/reviews-types' ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const GetPermitsDropdown = authenticate( _auth => fetchPromise( path( 'permits-types/dropdown' ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const GetFunds = authenticate( _auth => fetchPromise( path( 'funds' ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ).then( mapToDropdown ) );

export const GetFirms = authenticate( _auth => fetchPromise( path( 'firms' ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ).then( mapToDropdown ) );

export const SearchActionRequests = authenticate( ( _auth, query, filters = [], columns = [] ) => fetchPromise( path( 'action-requests/search', { query, filters, columns } ), {
	method  : 'GET',
	headers : _auth.defaultsHeaders,
} ).catch( () => [] ) );

// ***************** Developer & Utility Reviews Routes ***************** //

export const GetReviews = authenticate( ( _auth, filters = [], columns = [], start = 0, end = 15 ) => fetchPromise( path( 'developer-utility-reviews', {
	filters, columns, start, end
} ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const GetReview = authenticate( ( _auth, reviewId ) => fetchPromise( path( `developer-utility-reviews/${reviewId}` ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const DownloadReviewsCSV = authenticate( ( _auth, filters = [], columns = [] ) => fetchPromise( path( 'developer-utility-reviews/csv', { filters, columns } ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} )
	.then( csv => csvToBlob( 'reviews', csv ) ) );

export const DownloadReviewsPDF = authenticate( ( _auth, filters = [], columns = [] ) => fetchPromise( path( 'developer-utility-reviews/pdf', { filters, columns } ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} )
	.then( buffer => pdfToBlob( 'reviews', buffer ) ) );

export const CreateReview = authenticate( ( _auth, review ) => fetchPromise( path( 'developer-utility-reviews' ), {
	method  : 'POST',
	headers : new Headers( {
		'Auth-Token' : _auth.token,
	} ),
	body : review
} ) );

export const EditReview = authenticate( ( _auth, review, reviewId ) => fetchPromise( path( `developer-utility-reviews/${reviewId}` ), {
	method  : 'PUT',
	headers : new Headers( {
		'Auth-Token' : _auth.token,
	} ),
	body : review
} ) );

export const DeleteReview = authenticate( ( _auth, reviewId ) => fetchPromise( path( `developer-utility-reviews/delete/${reviewId}` ), {
	method  : 'PUT',
	headers : _auth.defaultHeaders,
} ) );

export const SearchReviews = authenticate( ( _auth, query, filters = [], columns = [] ) => fetchPromise( path( 'developer-utility-reviews/search', { query, filters, columns } ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

// ***************** Engineering Action Requests Routes ***************** //

export const GetRequests = authenticate( ( _auth, filters = [], columns = [], start = 0, end = 15 ) => fetchPromise( path( 'engineering-action-requests', {
	filters, columns, start, end
} ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const GetRequest = authenticate( ( _auth, requestId ) => fetchPromise( path( `engineering-action-requests/${requestId}` ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const DownloadRequestsCSV = authenticate( ( _auth, filters = [], columns = [] ) => fetchPromise( path( 'engineering-action-requests/csv', { filters, columns } ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} )
	.then( csv => csvToBlob( 'requests', csv ) ) );

export const DownloadRequestsPDF = authenticate( ( _auth, filters = [], columns = [] ) => fetchPromise( path( 'engineering-action-requests/pdf', { filters, columns } ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} )
	.then( buffer => pdfToBlob( 'requests', buffer ) ) );

export const CreateRequest = authenticate( ( _auth, request ) => fetchPromise( path( 'engineering-action-requests' ), {
	method  : 'POST',
	headers : new Headers( {
		'Auth-Token' : _auth.token,
	} ),
	body : request,
} ) );

export const EditRequest = authenticate( ( _auth, request, requestId ) => fetchPromise( path( `engineering-action-requests/${requestId}` ), {
	method  : 'PUT',
	headers : new Headers( {
		'Auth-Token' : _auth.token,
	} ),
	body : request,
} ) );

export const DeleteRequest = authenticate( ( _auth, requestId ) => fetchPromise( path( `engineering-action-requests/delete/${requestId}` ), {
	method  : 'PUT',
	headers : new Headers( {
		'Auth-Token' : _auth.token,
	} ),
} ) );

export const SearchRequests = authenticate( ( _auth, query, filters = [], columns = [] ) => fetchPromise( path( 'engineering-action-requests/search', { query, filters, columns } ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const GetNextRequestNumber = authenticate( _auth => fetchPromise( path( 'engineering-action-requests/next-number' ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const GetActionRequestsDropdown = authenticate( _auth => fetchPromise( path( 'engineering-action-requests/dropdown' ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

// ***************** Parcel Routes ***************** //

export const GetParcels = authenticate( ( _auth, filters = [], columns = [], start = 0, end = 15 ) => fetchPromise( path( 'parcels', {
	filters, columns, start, end
} ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const DownloadParcelsCSV = authenticate( ( _auth, filters = [], columns = [] ) => fetchPromise( path( 'parcels/csv', { filters, columns } ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} )
	.then( csv => csvToBlob( 'parcels', csv ) ) );

export const DownloadParcelsPDF = authenticate( ( _auth, filters = [], columns = [] ) => fetchPromise( path( 'parcels/pdf', { filters, columns } ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} )
	.then( buffer => pdfToBlob( 'parcels', buffer ) ) );

export const CreateParcel = authenticate( ( _auth, parcel ) => fetchPromise( path( 'parcels' ), {
	method  : 'POST',
	headers : _auth.defaultHeaders,
	body    : JSON.stringify( parcel ),
} ) );

export const SearchParcels = authenticate( ( _auth, query, filters = [], columns = [] ) => fetchPromise( path( 'parcels/search', { query, filters, columns } ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

// ***************** Permits Routes ***************** //

export const GetPermits = authenticate( ( _auth, filters = [], columns = [], start = 0, end = 15 ) => fetchPromise( path( 'permits', { filters, columns, start, end } ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const GetPermit = authenticate( ( _auth, permitId ) => fetchPromise( path( `permits/${permitId}` ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const DownloadPermitsCSV = authenticate( ( _auth, filters = [], columns = [] ) => fetchPromise( path( 'permits/csv', { filters, columns } ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} )
	.then( csv => csvToBlob( 'permits', csv ) ) );

export const DownloadPermitsPDF = authenticate( ( _auth, filters = [], columns = [] ) => fetchPromise( path( 'permits/pdf', { filters, columns } ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} )
	.then( buffer => pdfToBlob( 'permits', buffer ) ) );

export const DownloadPermitsTemplate = authenticate( ( _auth, permitsKey ) => fetch( path( `permits/${permitsKey}/pdf` ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} )
	.then( bufferToBlob ) );

export const CreatePermit = authenticate( ( _auth, permit ) => fetchPromise( path( 'permits' ), {
	method  : 'POST',
	headers : new Headers( {
		'Auth-Token' : _auth.token,
	} ),
	body : permit,
} ) );

export const EditPermit = authenticate( ( _auth, permit, permitId ) => fetchPromise( path( `permits/${permitId}` ), {
	method  : 'PUT',
	headers : new Headers( {
		'Auth-Token' : _auth.token,
	} ),
	body : permit,
} ) );

export const DeletePermit = authenticate( ( _auth, permitId ) => fetchPromise( path( `permits/delete/${permitId}` ), {
	method  : 'PUT',
	headers : new Headers( {
		'Auth-Token' : _auth.token,
	} )
} ) );

export const SearchPermits = authenticate( ( _auth, query, filters = [], columns = [] ) => fetchPromise( path( 'permits/search', { query, filters, columns } ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const GetNextPermitNumber = authenticate( _auth => fetchPromise( path( 'permits/next-number' ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );


// ***************** Agreement Routes ***************** //
export const GetAgreements = authenticate( ( _auth, filters = [], columns = [], start = 0, end = 15 ) => fetchPromise( path( 'agreements', {
	filters, columns, start, end
} ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const GetAgreement = authenticate( ( _auth, agreementId ) => fetchPromise( path( `agreements/${agreementId}` ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const GetNextAgreementNumber = authenticate( _auth => fetchPromise( path( 'agreements/next-number' ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const DownloadAgreementsCSV = authenticate( ( _auth, filters = [], columns = [] ) => fetchPromise( path( 'agreements/csv', { filters, columns } ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} )
	.then( csv => csvToBlob( 'agreements', csv ) ) );

export const DownloadAgreementsPDF = authenticate( ( _auth, filters = [], columns = [] ) => fetchPromise( path( 'agreements/pdf', { filters, columns } ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} )
	.then( buffer => pdfToBlob( 'agreements', buffer ) ) );

export const CreateAgreement = authenticate( ( _auth, agreement ) => fetchPromise( path( 'agreements' ), {
	method  : 'POST',
	headers : new Headers( {
		'Auth-Token' : _auth.token,
	} ),
	body : agreement,
} ) );

export const EditAgreement = authenticate( ( _auth, agreement, agreementsKey ) => fetchPromise( path( `agreements/${agreementsKey}` ), {
	method  : 'PUT',
	headers : new Headers( {
		'Auth-Token' : _auth.token,
	} ),
	body : agreement,
} ) );

export const DeleteAgreement = authenticate( ( _auth, agreementsKey ) => fetchPromise( path( `agreements/delete/${agreementsKey}` ), {
	method  : 'PUT',
	headers : new Headers( {
		'Auth-Token' : _auth.token,
	} ),
} ) );

export const SearchAgreements = authenticate( ( _auth, query, filters = [], columns = [] ) => fetchPromise( path( 'agreements/search', { query, filters, columns } ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const GetAgreementCategories = authenticate( _auth => fetchPromise( path( 'agreements/agreement-categories' ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

// ***************** Job Routes ***************** //

export const GetJobs = authenticate( ( _auth, filters = [], columns = [], start = 0, end = 15 ) => fetchPromise( path( 'jobs', {
	filters, columns, start, end
} ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const GetJob = authenticate( ( _auth, jobId ) => fetchPromise( path( `jobs/${jobId}` ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const GetNextJobNumber = authenticate( ( _auth, type ) => fetchPromise( path( `jobs/next-number/${type}` ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const DownloadJobsCSV = authenticate( ( _auth, filters = [], columns = [] ) => fetchPromise( path( 'jobs/csv', { filters, columns } ), {
	method  : 'GET',
	headers : _auth.defaultHeaders
} )
	.then( csv => csvToBlob( 'jobs', csv ) ) );

export const DownloadJobsPDF = authenticate( ( _auth, filters = [], columns = [] ) => fetchPromise( path( 'jobs/pdf', { filters, columns } ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} )
	.then( buffer => pdfToBlob( 'jobs', buffer ) ) );

export const GetJobsTypes = authenticate( _auth => fetchPromise( path( 'jobs-types' ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ).then( mapToDropdown ) );

export const CreateJob = authenticate( ( _auth, job ) => fetchPromise( path( 'jobs' ), {
	method  : 'POST',
	headers : new Headers( {
		'Auth-Token' : _auth.token,
	} ),
	body : job,
} ) );

export const CreateMiscJob = authenticate( ( _auth, job ) => fetchPromise( path( 'jobs-misc' ), {
	method  : 'POST',
	headers : new Headers( {
		'Auth-Token' : _auth.token,
	} ),
	body : job,
} ) );

export const EditJob = authenticate( ( _auth, job, jobsKey ) => fetchPromise( path( `jobs/${jobsKey}` ), {
	method  : 'PUT',
	headers : new Headers( {
		'Auth-Token' : _auth.token,
	} ),
	body : job,
} ) );

export const EditMiscJob = authenticate( ( _auth, job, jobsKey ) => fetchPromise( path( `jobs-misc/${jobsKey}` ), {
	method  : 'PUT',
	headers : new Headers( {
		'Auth-Token' : _auth.token,
	} ),
	body : job,
} ) );

export const DeleteJob = authenticate( ( _auth, jobsKey ) => fetchPromise( path( `jobs/delete/${jobsKey}` ), {
	method  : 'PUT',
	headers : new Headers( {
		'Auth-Token' : _auth.token,
	} ),
} ) );

export const SearchJobs = authenticate( ( _auth, query, filters = [], columns = [] ) => fetchPromise( path( 'jobs/search', { query, filters, columns } ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const AddMaterials = authenticate( ( _auth, material, jobsKey ) => fetchPromise( path( `jobs/${jobsKey}/materials` ), {
	method  : 'POST',
	headers : _auth.defaultHeaders,
	body    : JSON.stringify( { 'description' : material } ),
} ) );

export const GetMaterials = authenticate( ( _auth, jobsKey ) => fetchPromise( path( `jobs/${jobsKey}/materials` ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

// ***************** Facility Routes ***************** //

export const GetFacilities = authenticate( ( _auth, filters = [], columns = [], start = 0, end = 15 ) => fetchPromise( path( 'facilities', {
	filters, columns, start, end
} ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const GetFacility = authenticate( ( _auth, facilityId ) => fetchPromise( path( `facilities/${facilityId}` ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const CreateFacility = authenticate( ( _auth, facility ) => fetchPromise( path( 'facilities' ), {
	method  : 'POST',
	headers : new Headers( {
		'Auth-Token' : _auth.token,
	} ),
	body : facility,
} ) );

export const EditFacility = authenticate( ( _auth, facility, facilitiesKey ) => fetchPromise( path( `facilities/${facilitiesKey}` ), {
	method  : 'PUT',
	headers : new Headers( {
		'Auth-Token' : _auth.token,
	} ),
	body : facility,
} ) );

export const DeleteFacility = authenticate( ( _auth, facilitiesKey ) => fetchPromise( path( `facilities/delete/${facilitiesKey}` ), {
	method  : 'PUT',
	headers : new Headers( {
		'Auth-Token' : _auth.token,
	} ),
} ) );

export const DownloadFacilitiesCSV = authenticate( ( _auth, filters = [], columns = [] ) => fetchPromise( path( 'facilities/csv', { filters, columns } ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} )
	.then( csv => csvToBlob( 'facilities', csv ) ) );

export const DownloadFacilitiesPDF = authenticate( ( _auth, filters = [], columns = [] ) => fetchPromise( path( 'facilities/pdf', { filters, columns } ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} )
	.then( buffer => pdfToBlob( 'facilities', buffer ) ) );

export const SearchFacilities = authenticate( ( _auth, query, filters = [], columns = [] ) => fetchPromise( path( 'facilities/search', { query, filters, columns } ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const GetFacilityJobs = authenticate( ( _auth, facilityId ) => fetchPromise( path( `facilities/${facilityId}/jobs` ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const GetFacilityAgreements = authenticate( ( _auth, facilityId ) => fetchPromise( path( `facilities/${facilityId}/agreements` ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const GetFacilityPermits = authenticate( ( _auth, facilityId ) => fetchPromise( path( `facilities/${facilityId}/permits` ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

// ***************** Email Routes ***************** //


// * Map * //

export const getGeoJSON = authenticate( ( _auth, key ) => fetchPromise( path( `map/geo-json/${key}` ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const SearchMap = authenticate( ( _auth, query ) => fetchPromise( path( 'map/search', { query } ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

export const GetMapRecords = authenticate( _auth => fetchPromise( path( 'map/records' ), {
	method  : 'GET',
	headers : _auth.defaultHeaders,
} ) );

// ***************** Email ***************** //

export const SendEmail = authenticate( ( _auth, sender, recipients, type, data ) => fetchPromise( path( 'emails' ), {
	method  : 'POST',
	headers : _auth.defaultHeaders,
	body    : JSON.stringify( {
		sender,
		recipients,
		type,
		data
	} )
} ) );

// ***************** Reports ***************** //

export const DownloadReport = authenticate( ( _auth, reportId, downloadType, name = 'test' ) => fetchPromise( path( `reports/download/${reportId}` ), {
	method  : 'POST',
	headers : _auth.defaultHeaders,
	body    : JSON.stringify( {
		downloadType,
	} ),
} ).then( ( res ) => {
	if ( downloadType === 'CSV' ) {
		return csvToBlob( name, res );
	}
	if ( downloadType === 'PDF' ) {
		// console.log( 'downloading' );
		return pdfToBlob( name, res );
	}
	return pdfToBlob( name, res );
} ) );

export const ImportGis = authenticate( _auth => fetchPromise( path( 'reports/import-gis' ), {
	method  : 'POST',
	headers : _auth.defaultHeaders,
} ) );

export const postError = authenticate( ( _auth, ...error ) => fetchPromise( path( 'postError' ), {
	method  : 'POST',
	headers : _auth.defaultHeaders,
	body    : stringify( error ),
} ) );

export const DeleteDocument = authenticate( ( _auth, filesKey, delDocPayload ) => fetchPromise( path( `document/delete/${filesKey}` ), {
	method  : 'POST',
	headers : _auth.defaultHeaders,
	body    : stringify( delDocPayload ),
} ) );
